/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import axios, { AxiosError, Method, ResponseType } from "axios";
import { Dispatch } from "redux";
import { AsgardeoSPAClient, Hooks } from "@asgardeo/auth-react";

import Settings from "../conf/settings";
import {
  fetchApiRequest,
  fetchApiSuccess,
  fetchApiFailure,
  fetchThumbnailRequest,
  fetchThumbnailSuccess,
  fetchThumbnailFailure,
  fetchThumbnailListSuccess,
  fetchThumbnailListRequest,
  fetchApiListRequest,
  fetchApiListSuccess,
  fetchApiListFailure,
  fetchMajorVersionApiListRequest,
  fetchMajorVersionApiListSuccess,
  fetchMajorVersionApiListFailure,
  fetchFeaturedApiListSuccess,
  fetchFeaturedApiListRequest,
  fetchFeaturedApiListFailure,
} from "../store/actions/api";
import {
  fetchGraphqlRequest,
  fetchGraphqlRequestSuccess,
  fetchGraphqlFailure,
} from "../store/actions/graphql";
import {
  createApiTestKeyFailure,
  createApiTestKeyRequest,
  createApiTestKeySuccess,
  createApikeyFailure,
  createApikeyRequest,
  createApikeySuccess,
  createKeysFailure,
  createKeysRequest,
  createKeysSuccess,
  createOauthTokenFailure,
  createOauthTokenRequest,
  createOauthTokenSuccess,
  fetchKeysFailure,
  fetchKeysRequest,
  fetchKeysSuccess,
  mapApplicationKeyFailure,
  mapApplicationKeyRequest,
  mapApplicationKeySucess,
  updateKeysFailure,
  updateKeysRequest,
  updateKeysSuccess,
} from "../store/actions/applicationKeys";
import {
  createApplicationFailure,
  createApplicationRequest,
  createApplicationSuccess,
  deleteApplicationFailure,
  deleteApplicationRequest,
  deleteApplicationSuccess,
  fetchApplicationDetailsFailure,
  fetchApplicationDetailsRequest,
  fetchApplicationDetailsSuccess,
  fetchApplicationFailure,
  fetchApplicationRequest,
  fetchApplicationSuccess,
  shareApplicationFailure,
  shareApplicationRequest,
  shareApplicationSuccess,
  updateApplicationFailure,
  updateApplicationRequest,
  updateApplicationSuccess,
} from "../store/actions/applications";
import {
  deleteApiCommentsFailure,
  deleteApiCommentsRequest,
  deleteApiCommentsSuccess,
  editApiCommentsFailure,
  editApiCommentsRequest,
  editApiCommentsSuccess,
  fetchApiCommentsFailure,
  fetchApiCommentsRequest,
  fetchApiCommentsSuccess,
  postApiCommentsFailure,
  postApiCommentsRequest,
  postApiCommentsSuccess,
} from "../store/actions/comments";
import {
  createApiKeysCredentialsFailure,
  createApiKeysCredentialsRequest,
  createApiKeysCredentialsSuccess,
  deleteApiKeysCredentialsFailure,
  deleteApiKeysCredentialsRequest,
  deleteApiKeysCredentialsSuccess,
  fetchApiKeysCredentialsFailure,
  fetchApiKeysCredentialsRequest,
  fetchApiKeysCredentialsSuccess
} from "../store/actions/credentials";
import {
  fetchDocumentsFailure,
  fetchDocumentsRequest,
  fetchDocumentsSuccess,
} from "../store/actions/documents";
import {
  resetNotification,
  setErrorNotification,
  setSucessNotification,
} from "../store/actions/notification";
import {
  createRatingsFailure,
  createRatingsRequest,
  createRatingsSuccess,
  fetchRatingsFailure,
  fetchRatingsRequest,
  fetchRatingsSuccess,
} from "../store/actions/rating";
import {
  fetchSdkLanguagesFailure,
  fetchSdkLanguagesRequest,
  fetchSdkLanguagesSuccess,
} from "../store/actions/sdk";
import {
  createSubscriptionsFailure,
  createSubscriptionsRequest,
  createSubscriptionsSuccess,
  deleteSubscriptionsFailure,
  deleteSubscriptionsRequest,
  deleteSubscriptionsSuccess,
  fetchSubscriptionRequest,
  fetchSubscriptionsFailure,
  fetchSubscriptionsSuccess,
  updateSubscriptionFailure,
  updateSubscriptionRequest,
  updateSubscriptionSuccess,
} from "../store/actions/subscriptions";
import {
  fetchSwaggerFailure,
  fetchSwaggerRequest,
  fetchSwaggerSuccess,
} from "../store/actions/swagger";
import {
  userLoginRequest,
  userLoginSuccess,
  requireLogin,
  fetchUserInfoSuccess,
  fetchUserInfoFailure,
  fetchUserOrgInfoRequest,
  fetchUserOrgInfoSuccess,
  fetchUserOrgInfoFailure,
  changeUserOrganizationRequest,
  changeUserOrganizationSuccess,
  changeUserOrganizationFailure,
  obtainApimTokenRequest,
  obtainApimTokenSuccess,
  obtainApimTokenFailure,
  addEnterpriseUserRequest,
  addEnterpriseUserSuccess,
  addEnterpriseUserFailure,
} from "../store/actions/user";
import {
  fetchIDPRequest,
  fetchIDPSuccess,
  fetchIDPFailure,
} from "../store/actions/idps";
import store from "../store/index";

import * as Constants from "./constants";
import {
  Key,
  Comment,
  ApiRating,
  EndpointURL,
  Subscription,
  KeyType,
  CreateComment,
  Api,
  ApiList,
  ApiKeyAdditionalProperties,
  Application,
  Organization,
  StorageOperations,
  Pagination,
  ChoreoUserInfo,
  AdditionalProperty,
  ApiScope,
  ApplicationKeyMapping,
  ApplicationShare,
  DevPortalAppEnv,
  ApiKeyGenerationRequest,
  Environment,
  ApiKeyRegenerationRequest,
} from "./interfaces";
import {
  fetchApiTagsFailure,
  fetchApiTagsRequest,
  fetchApiTagsSuccess,
} from "../store/actions/tags";
import {
  fetchApiCategoriesFailure,
  fetchApiCategoriesRequest,
  fetchApiCategoriesSuccess,
} from "../store/actions/categories";
import * as Utils from "./utils";
import {
  addExternalUserToOrgFailure,
  addExternalUserToOrgRequest,
  addExternalUserToOrgSuccess,
} from "../store/actions/addExternalUserToOrg";
import {
  trackFetchUserOrgFailure,
  trackFetchUserOrgSuccess,
  trackIdpAuthFailure,
  trackIdpAuthInitiation,
  trackIdpAuthSuccess,
  trackObtainApimTokenFailure,
  trackObtainApimTokenInitiation,
  trackObtainApimTokenSuccess,
} from '../util/app-insights';
import { isSelfSignupEnabled } from "./utils";
import {
  fetchCustomDomainOrganizationFailure,
  fetchCustomDomainOrganizationRequest,
  fetchCustomDomainOrganizationSuccess
} from "../store/actions/customDomainOrganization";
import {
  fetchOrganizationMetadataFailure,
  fetchOrganizationMetadataRequest,
  fetchOrganizationMetadataSuccess
} from "../store/actions/organizationMetadata";
import {
  fetchSubscribedApiScopesFailure,
  fetchSubscribedApiScopesRequest,
  fetchSubscribedApiScopesSuccess
} from "../store/actions/subscribedApiScopes";
import { APPROVAL_STATUS_APPROVED, APPROVAL_STATUS_APPROVED_CODE, MAJOR_VERSION, QUERY_PARAM_VALUE_OWNER_INFO, USER_NOT_FOUND, USER_NOT_FOUND_CODE } from './constants';
import { fetchSubscriptionPoliciesFailure, fetchSubscriptionPoliciesRequest, fetchSubscriptionPoliciesSuccess } from "../store/actions/subscriptionPolicies";
import { fetchApiKeysFailure, fetchApiKeysRequest, fetchApiKeysSuccess, generateApiKeyV2Failure, generateApiKeyV2Request, generateApiKeyV2Success, regenerateApiKeyV2Failure, regenerateApiKeyV2Request, regenerateApiKeyV2Success, revokeApiKeyV2Failure, revokeApiKeyV2Request, revokeApiKeyV2Success } from "../store/actions/apiKeys";

const INT_MAX = 2147483647;

const authClient = AsgardeoSPAClient.getInstance();
const defaultHeaders = {
  Accept: "application/json",
};

const getOrgIdQueryParam = () => {
  let organizationId;
  if (store.getState().userReducer.selectedOrg.uuid) {
    organizationId = store.getState().userReducer.selectedOrg.uuid;
  }
  return { organizationId: organizationId };
};

/*
 * API definition (OpenAPI / AsyncAPI) fetches can overlap: the initial API load fetches the
 * definition without an environment while the overview and try-out pages fetch the definition
 * of the selected gateway environment. Only the response of the most recent request is applied
 * to the store so that a slower, outdated response cannot overwrite a newer one.
 */
let latestDefinitionRequestId = 0;

const startDefinitionRequest = (): number => {
  latestDefinitionRequestId += 1;
  return latestDefinitionRequestId;
};

const isLatestDefinitionRequest = (requestId: number): boolean => {
  return requestId === latestDefinitionRequestId;
};

const getDefinitionResource = (apiType?: string): string => {
  return apiType === Constants.API_TYPE_WS
    ? Constants.RESOURCE_ASYNCAPI
    : Constants.RESOURCE_SWAGGER;
};

const getApiResourceAuthenticated = async (
  apiId = "",
  apiResource?: string,
  queryParams = getOrgIdQueryParam(),
  responseType: ResponseType = "json",
  reqHeaders = defaultHeaders
) => {
  return authClient?.httpRequest({
    url: Utils.getApiResourceEndpoint(apiId, apiResource),
    method: "GET",
    headers: reqHeaders,
    responseType: responseType,
    params: queryParams,
  });
};

const getApiResourceUnauthenticated = async (
  apiId: string,
  apiResource?: string,
  queryParams = {},
  responseType: ResponseType = "json",
  reqHeaders = defaultHeaders
) => {
  return axios.get(Utils.getApiResourceEndpoint(apiId, apiResource), {
    headers: reqHeaders,
    responseType: responseType,
    params: queryParams,
  });
};

export const getOrganizationMappingForCustomDomain = async (
    queryParams = {},
    responseType: ResponseType = "json",
    reqHeaders = defaultHeaders
) => {
  return axios.get(Utils.getCustomDomainOrgHandleMappingEndpoint(), {
    headers: reqHeaders,
    responseType: responseType,
    params: queryParams,
  });
};

export const getOrganizationMetadata = async (
    queryParams = {},
    responseType: ResponseType = "json",
    reqHeaders = defaultHeaders
) => {
  return axios.get(Utils.getOrganizationMetadataEndpoint(), {
    headers: reqHeaders,
    responseType: responseType,
    params: queryParams,
  });
};

const getUserInfoAuthenticated = async () => {
  return authClient?.httpRequest({
    url: Utils.getUserInfoEndpoint(),
    method: "GET",
    headers: defaultHeaders,
  });
};

const getGraphqlResponse = async () => {
  const additionalProperties = store.getState().apiReducer.api.additionalProperties;
  const projectIdObject: AdditionalProperty = additionalProperties?.find((additionalProperty: AdditionalProperty) => {
    return additionalProperty.name === 'projectId'
  });
  const projectId = projectIdObject?.value;
  let graphQlQuery = '';
  if (projectId) {
    graphQlQuery = `query {
                environments(
                  orgUuid:"${ store.getState().userReducer.selectedOrg.uuid }",
                  projectId: "${ projectId }"
                ){
                  organizationUuid,
                  orgShared,
                  name,
                  description,
                  id,
                  templateId,
                  vhost,
                  choreoEnv,
                  projectId,
                  promoteFrom,
                  namespace,
                  externalApiEnvName,
                  internalApiEnvName,
                  sandboxApiEnvName,
                  templateId,
                }
              }`
  } else {
    graphQlQuery = `query {
                environments(orgUuid:"${ store.getState().userReducer.selectedOrg.uuid }"){
                  organizationUuid,
                  orgShared,
                  name,
                  description,
                  id,
                  templateId,
                  vhost,
                  choreoEnv,
                  projectId,
                  promoteFrom,
                  namespace,
                }
              }`
  }

  return authClient?.httpRequest({
    url: Utils.getGraphqlEndpoint(),
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    data: {
      query: graphQlQuery,
    },
  });
};

export const fetchCustomDomainOrganizationMapping = (customDomain: string) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(fetchCustomDomainOrganizationRequest());
      const queryParams = { domain: customDomain };

      const response = await getOrganizationMappingForCustomDomain(queryParams);
      if (response) {
        dispatch(fetchCustomDomainOrganizationSuccess(response.data));
      }
    } catch (error) {
      dispatch(
          setErrorNotification("Error while retrieving organization for the custom domain")
      );
      dispatch(fetchCustomDomainOrganizationFailure(error));
    }
  };

export const fetchOrganizationMetadata = (orgHandle = '', customDomain = '') =>
    async (dispatch: Dispatch) => {
      try {
        dispatch(fetchOrganizationMetadataRequest());
        const queryParams = { domain: customDomain, orghandle: orgHandle };

        const response = await getOrganizationMetadata(queryParams);
        if (response) {
          dispatch(fetchOrganizationMetadataSuccess(response.data.organizationMetadata));
        }
      } catch (error) {
        dispatch(
            setErrorNotification("Error while retrieving organization metadata")
        );
        dispatch(fetchOrganizationMetadataFailure(error));
      }
    };

export const fetchGraphqlResponse = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchGraphqlRequest());
      const graphqlResponse = await getGraphqlResponse();
      if (graphqlResponse) {
        dispatch(fetchGraphqlRequestSuccess(graphqlResponse.data.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while getting grphql response"));
      dispatch(fetchGraphqlFailure(error));
    }
  };
};

const updateApiResourceAuthenticated = async (
  apiId: string,
  apiResource?: string,
  queryParams = {},
  resourceData?: Comment | ApiRating | CreateComment,
  httpMethod?: Method
) => {
  return authClient?.httpRequest({
    url: Utils.getApiResourceEndpoint(apiId, apiResource),
    method: httpMethod,
    headers: {
      Accept: "application/json",
    },
    data: resourceData,
    params: queryParams,
  });
};

const updateSubscriptionAuthenticated = async (
  subscriptionId: string,
  resourceData?: Subscription,
  queryParams = {}
) => {
  return authClient?.httpRequest({
    url: Utils.getSubscriptionResourceEndpoint(subscriptionId),
    method: "PUT",
    headers: defaultHeaders,
    data: resourceData,
    params: queryParams,
  });
};

const getSubscriptionPoliciesList = async (queryParams = {}) => {
  return authClient?.httpRequest({
    url: Utils.getSubscriptionThrottlingPoliciesEndpoint(),
    method: "GET",
    headers: defaultHeaders,
    params: queryParams,
  });
};

const getApiListAuthenticated = async (
  pagination?: string,
  queryParams = {}
) => {
  return authClient?.httpRequest({
    url: Utils.getApiListEndpoint(pagination),
    method: "GET",
    headers: defaultHeaders,
    params: queryParams,
  });
};

const getApiListUnAuthenticated = async (
    pagination?: string,
    queryParams = {},
    responseType: ResponseType = "json",
    reqHeaders = defaultHeaders
) => {
  return axios.get(Utils.getApiListEndpoint(pagination), {
    headers: reqHeaders,
    responseType: responseType,
    params: queryParams,
  });
};

const getApiTagsAuthenticated = async (queryParams = {}) => {
  return authClient?.httpRequest({
    url: Utils.getApiTagsEndpoint(),
    method: "GET",
    headers: defaultHeaders,
    params: queryParams,
  });
};

const getApiCategoriesAuthenticated = async (queryParams = {}) => {
  return authClient?.httpRequest({
    url: Utils.getApiCategoryEndpoint(),
    method: "GET",
    headers: defaultHeaders,
    params: queryParams,
  });
};

const getUserOrganizationList = async () => {
  return authClient?.httpRequest({
    url: Utils.getUserValidationEndpoint(),
    method: "GET",
    headers: defaultHeaders,
  });
};

const addEnterpriseUser = async () => {
  return authClient?.httpRequest({
    url: Utils.getAddEnterpriseUserEndpoint(),
    method: "POST",
    headers: defaultHeaders,
    data: {
      isDevportalUser: true
    }
  });
};

export const fetchApiUnauthenticated = (
  apiId: string,
  organizationId: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiRequest());
      const apiResponse = await getApiResourceUnauthenticated(
        apiId,
        undefined,
        { organizationId: organizationId }
      );
      if (apiResponse) {
        dispatch(fetchApiSuccess(apiResponse.data));
        // Running all the required API calls in parallel

        // Get API Comments
        dispatch(fetchApiCommentsRequest());
        getApiResourceUnauthenticated(
          apiId,
          Constants.RESOURCE_API_COMMENTS,
          { organizationId: organizationId }
        ).then((commentsResponse) => {
          if (commentsResponse) {
            // Reversing the comments to list them in inverse chronological order
            dispatch(fetchApiCommentsSuccess(commentsResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving comments"));
          dispatch(fetchApiCommentsFailure(error));
        });

        // Get API documents
        dispatch(fetchDocumentsRequest());
        getApiResourceUnauthenticated(
          apiId,
          Constants.RESOURCE_API_DOCUMENTS,
          { organizationId: organizationId }
        ).then((documentsResponse)=>{
          if (documentsResponse) {
            dispatch(fetchDocumentsSuccess(documentsResponse.data));
          }
        }).catch((error) => {
          dispatch(
            setErrorNotification("Error while retrieving API documents")
          );
          dispatch(fetchDocumentsFailure(error));
        });

        // Get API thumbnail
        dispatch(fetchThumbnailRequest());
        getApiResourceUnauthenticated(
          apiId,
          Constants.RESOURCE_THUMBNAIL,
          { organizationId: organizationId },
          "blob"
        ).then((thumbnailResponse)=>{
          if (thumbnailResponse) {
            dispatch(fetchThumbnailSuccess(thumbnailResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving API thumbnail"));
          dispatch(fetchThumbnailFailure(error));
        });
        
        // Get API swagger
        dispatch(fetchSwaggerRequest());
        const definitionRequestId = startDefinitionRequest();
        let queryParams = {};
          if (apiResponse.data.endpointURLs.length > 0) {
            const environments = apiResponse.data.endpointURLs.map(
              (endpoint: EndpointURL) => {
                return endpoint.environmentName;
              }
            );
            queryParams = {
              environmentName: environments[0],
            };
          }
        getApiResourceUnauthenticated(
          apiId,
          getDefinitionResource(apiResponse.data.type),
          { ...queryParams, organizationId: organizationId },
          "json"
        ).then((swaggerResponse)=>{
          if (swaggerResponse && isLatestDefinitionRequest(definitionRequestId)) {
            dispatch(fetchSwaggerSuccess(swaggerResponse.data));
          }
        }).catch((error) => {
          if (!isLatestDefinitionRequest(definitionRequestId)) {
            return;
          }
          dispatch(setErrorNotification("Error while retrieving OpenAPI definition"));
          dispatch(fetchSwaggerFailure(error));
        });
      }
    } catch (error) {
      const { response } = error;
      if (response?.status === Constants.RESOURCE_NOT_FOUND) {
        dispatch(requireLogin());
      }
      dispatch(setErrorNotification("Error while retrieving API"));
      dispatch(fetchApiFailure(error));
    }
  };
};

export const fetchApiAuthenticated = (
  apiId: string,
  organizationId: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      // Get API
      dispatch(fetchApiRequest());
      const apiResponse = await getApiResourceAuthenticated(apiId, undefined, {
        organizationId: organizationId,
      });
      if (apiResponse) {
        dispatch(fetchApiSuccess(apiResponse.data));
        // Running all the required API calls in parallel

        // Get API comments
        dispatch(fetchApiCommentsRequest());
        getApiResourceAuthenticated(
          apiId,
          Constants.RESOURCE_API_COMMENTS,
          { organizationId: organizationId }
        ).then((commentsResponse) => {
          if (commentsResponse) {
            dispatch(fetchApiCommentsSuccess(commentsResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving comments"));
          dispatch(fetchApiCommentsFailure(error));
        });

        // Get API documents
        dispatch(fetchDocumentsRequest());
        getApiResourceAuthenticated(
          apiId,
          Constants.RESOURCE_API_DOCUMENTS,
          { organizationId: organizationId }
        ).then((documentsResponse) => {
          if (documentsResponse) {
            dispatch(fetchDocumentsSuccess(documentsResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving API documents"));
          dispatch(fetchDocumentsFailure(error));
        });

        // Get API rating
        dispatch(fetchRatingsRequest());
        getApiResourceAuthenticated(
          apiId,
          Constants.RESOURCE_API_RATINGS,
          { organizationId: organizationId }
        ).then((ratingsResponse) => {
          if (ratingsResponse) {
            dispatch(fetchRatingsSuccess(ratingsResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving API rating"));
          dispatch(fetchRatingsFailure(error));
        });

        // Get API thumbnail
        dispatch(fetchThumbnailRequest());
        getApiResourceAuthenticated(
          apiId,
          Constants.RESOURCE_THUMBNAIL,
          { organizationId: organizationId },
          "blob"
        ).then((apiResponse) => {
          if (apiResponse) {
            dispatch(fetchThumbnailSuccess(apiResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving API thumbnail"));
          dispatch(fetchThumbnailFailure(error));
        });

        // Get API Swagger definition
        dispatch(fetchSwaggerRequest());
        const definitionRequestId = startDefinitionRequest();
        const queryParams = {};
        getApiResourceAuthenticated(
          apiId,
          getDefinitionResource(apiResponse.data.type),
          { ...queryParams, organizationId: organizationId },
          "json"
        ).then((swaggerResponse) => {
          if (swaggerResponse && isLatestDefinitionRequest(definitionRequestId)) {
            dispatch(fetchSwaggerSuccess(swaggerResponse.data));
          }
        }).catch((error) => {
          if (!isLatestDefinitionRequest(definitionRequestId)) {
            return;
          }
          dispatch(setErrorNotification("Error while retrieving OpenAPI definition"));
          dispatch(fetchSwaggerFailure(error));
        });

        // Get SDK Languages
        dispatch(fetchSdkLanguagesRequest());
        getSdkLanguagesResource(
          organizationId
        ).then((sdkLanguageResponse) => {
          if (sdkLanguageResponse) {
            dispatch(fetchSdkLanguagesSuccess(sdkLanguageResponse.data));
          }
        }).catch((error) => {
          dispatch(setErrorNotification("Error while retrieving SDK languages"));
          dispatch(fetchSdkLanguagesFailure(error));
        });
      }
    } catch (error) {
      const { response } = error;
      if (response?.status === Constants.RESOURCE_NOT_FOUND) {
        dispatch(setErrorNotification("Requested API is not available"));
      } else {
        dispatch(setErrorNotification("Error while retrieving API"));
      }
      dispatch(fetchApiFailure(error));
    }
  };
};

export const fetchSubscribedApiScopes = (
    organizationId: string,
    apiIds: string[],
) => {
  return async (dispatch: Dispatch): Promise<string[] | null> => {
    const scopes: string[] = [];
    try {
      dispatch(fetchSubscribedApiScopesRequest());
      for (const apiId of apiIds) {
        const apiResponse = await getApiResourceAuthenticated(apiId, undefined, {
          organizationId: organizationId,
        });
        if (apiResponse) {
          apiResponse.data.scopes.map((scope: ApiScope) => { scopes.push(scope.key) });
        }
      }
      dispatch(fetchSubscribedApiScopesSuccess(scopes));
      return scopes;
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving subscribed api scopes"));
      dispatch(fetchSubscribedApiScopesFailure(error));
      return null;
    }
  };
};

export const fetchAllApisOfMajorVersionAuthenticated = (
  organizationId: string,
  apiName: string,
  apiMajorVersion: string
  ) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchMajorVersionApiListRequest());
      const apiResponse = await getApiListAuthenticated(undefined, {
        organizationId: organizationId,
        query: `name:"${apiName}" version:${apiMajorVersion}*`,
      });

      if (apiResponse) {
        dispatch(fetchMajorVersionApiListSuccess(apiResponse.data));
      } else {
        dispatch(fetchMajorVersionApiListSuccess({
          count: 0,
          list: [],
          pagination: {
            limit: 0,
            offset: 0,
            total: 0,
            next: "",
            previous: ""
          }
        }));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving all APIs of the API"));
      dispatch(fetchMajorVersionApiListFailure(error));
    }
  };
}

export const fetchApiListByMajorVersionAuthenticated = (
  organizationId: string,
  pagination?: string,
  requireThumbnails = true,
) => {
  return async (dispatch: Dispatch) => fetchApiListAuthenticated(
    organizationId,
    pagination,
    requireThumbnails,
    MAJOR_VERSION
  )(dispatch);
}

export const fetchApiListByMajorVersionUnAuthenticated = (
  organizationId: string,
  pagination?: string,
  requireThumbnails = true,
) => {
  return async (dispatch: Dispatch) => fetchApiListUnAuthenticated(
    organizationId,
    pagination,
    requireThumbnails,
    MAJOR_VERSION
  )(dispatch);
}

export const fetchSubscriptionPoliciesList = (organizationId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchSubscriptionPoliciesRequest());
      const response = await getSubscriptionPoliciesList({
        organizationId: organizationId,
      });
      if (response && response.data) {
        dispatch(fetchSubscriptionPoliciesSuccess(response.data));
      }
    } catch (error) {
      dispatch(
        setErrorNotification(
          "Error while retrieving the list of subscription policies"
        )
      );
      dispatch(fetchSubscriptionPoliciesFailure(error));
    }
  };
};

export const fetchApiListAuthenticated = (
  organizationId: string,
  pagination?: string,
  requireThumbnails = true,
  aggregateBy?: string,
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiListRequest());
      const apiResponse = await getApiListAuthenticated(pagination, {
        organizationId: organizationId,
        aggregateBy,
      });

      if (apiResponse) {
        dispatch(fetchApiListSuccess(apiResponse.data));
        const apiList: ApiList = apiResponse.data;

        if (requireThumbnails && apiList.list.length > 0) {
          const thumbnailMap: { [key: string]: string } = {};
          dispatch(fetchThumbnailListRequest());
          apiList.list.map(api => {
            getApiResourceAuthenticated(
              api.id,
              Constants.RESOURCE_THUMBNAIL,
              { organizationId: organizationId },
              "blob"
            ).then(
              apiThumbnailResponse => {
                if (apiThumbnailResponse) {
                  thumbnailMap[api.id] = URL.createObjectURL(apiThumbnailResponse.data); 
                  dispatch(fetchThumbnailListSuccess(thumbnailMap));
                }
              }
            ).catch((error) => {
              dispatch(
                setErrorNotification("Error while retrieving API thumbnail")
              );
            })
          });
        }
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving API list"));
      dispatch(fetchApiListFailure(error));
    }
  };
};

export const fetchFeaturedApiListAuthenticated = (
  organizationId: string,
  pagination?: string,
  requireThumbnails = true,
  aggregateBy?: string,
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchFeaturedApiListRequest());
      const apiResponse = await  getApiListAuthenticated(pagination, {
        organizationId: organizationId,
        aggregateBy,
      });
      if (apiResponse) {
        dispatch(fetchFeaturedApiListSuccess(apiResponse.data));
        const featuredApiList: ApiList = apiResponse.data;
        if (requireThumbnails && featuredApiList.list.length > 0) {
          const thumbnailMap: { [key: string]: string } = {};
          dispatch(fetchThumbnailListRequest());
          featuredApiList.list.map(api => {
            getApiResourceAuthenticated(
              api.id,
              Constants.RESOURCE_THUMBNAIL,
              { organizationId: organizationId },
              "blob"
            ).then(
              apiThumbnailResponse => {
                if (apiThumbnailResponse) {
                  thumbnailMap[api.id] = URL.createObjectURL(apiThumbnailResponse.data); 
                  dispatch(fetchThumbnailListSuccess(thumbnailMap));
                }
              }
            ).catch((error) => {
              dispatch(
                setErrorNotification(
                  "Error while retrieving API thumbnail"
                )
              );
            })
          });
        }
      }
    } catch (error) {
      dispatch(
        setErrorNotification(
          "Error while retrieving featured API list"
        )
      );
      dispatch(fetchFeaturedApiListFailure(error));
    }
  };
};

export const fetchApiListUnAuthenticated = (
    organizationId: string,
    pagination?: string,
    requireThumbnails = true,
    aggregateBy?: string,
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiListRequest());
      const apiResponse = await getApiListUnAuthenticated(pagination, {
        organizationId: organizationId,
        aggregateBy
      });

      if (apiResponse) {
        dispatch(fetchApiListSuccess(apiResponse.data));
        const apiList: ApiList = apiResponse.data;

        if (requireThumbnails && apiList.list.length > 0) {
          const thumbnailMap: { [key: string]: string } = {};
          dispatch(fetchThumbnailListRequest());
          apiList.list.map(api => {
            getApiResourceUnauthenticated(
              api.id,
              Constants.RESOURCE_THUMBNAIL,
              { organizationId: organizationId },
              "blob"
            ).then(
              apiThumbnailResponse => {
                if (apiThumbnailResponse) {
                  thumbnailMap[api.id] = URL.createObjectURL(apiThumbnailResponse.data); 
                  dispatch(fetchThumbnailListSuccess(thumbnailMap));
                }
              }
            ).catch((error) => {
              dispatch(
                setErrorNotification("Error while retrieving API thumbnail")
              );
            })
          });
        }
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving API list"));
      dispatch(fetchApiListFailure(error));
    }
  };
};

export const fetchApiTagList = (organizationId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiTagsRequest());
      const apiResponse = await getApiTagsAuthenticated({
        organizationId: organizationId,
      });
      if (apiResponse) {
        dispatch(fetchApiTagsSuccess(apiResponse.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving tags list"));
      dispatch(fetchApiTagsFailure(error));
    }
  };
};

export const fetchApiCategoryList = (organizationId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiCategoriesRequest());
      const apiResponse = await getApiCategoriesAuthenticated({
        organizationId: organizationId,
      });
      if (apiResponse) {
        dispatch(fetchApiCategoriesSuccess(apiResponse.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving category list"));
      dispatch(fetchApiCategoriesFailure(error));
    }
  };
};

export const fetchApiDetails = (apiId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiRequest());
      const apiResponse = await getApiResourceAuthenticated(apiId);
      if (apiResponse) {
        dispatch(fetchApiSuccess(apiResponse.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while fetching API details"));
      dispatch(fetchApiFailure(error));
    }
  };
};

export const fetchUserInfo = (
  orgHandle: string,
  userIdpId: string,
  reqHeaders = defaultHeaders
) => {
  return authClient?.httpRequest({
    url: Utils.getChoreoUserInfoEndpoint(orgHandle, userIdpId),
    method: "GET",
    headers: reqHeaders,
  });
};

export const fetchApplicationList = (reqHeaders = defaultHeaders) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApplicationRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(),
        method: "GET",
        headers: reqHeaders,
        // Get all the applications and filter the internal apps
        // Need to do this to show the correct application count
        params:  {...getOrgIdQueryParam(), limit: INT_MAX},
      });
      const defaultApplicationPrefix = Constants.DEFAULT_APP_INTERNAL_PREFIX;
      const apps = response?.data.list.filter((app: Application) => {
        return !app.name.includes(defaultApplicationPrefix, 0);
      });
      dispatch(
        fetchApplicationSuccess({ ...response?.data, list: apps, count: apps.length })
      );
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving application list"));
      dispatch(fetchApplicationFailure(error));
    }
  };
};

export const searchApplications = (query: string, reqHeaders = defaultHeaders) => {
  return authClient?.httpRequest({
    url: Utils.getApplicationEndpoint(),
    method: "GET",
    headers: reqHeaders,
    params: { ...getOrgIdQueryParam(), limit: INT_MAX, query },
  });
};

export const fetchApplicationDetails = (
  applicationId: string,
  reqHeaders = defaultHeaders
) => {
  const queryParam = {
    ... getOrgIdQueryParam(),
    include: QUERY_PARAM_VALUE_OWNER_INFO,
  }
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApplicationDetailsRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(applicationId),
        method: "GET",
        headers: reqHeaders,
        params: queryParam,
      });
      const updatedApps = [response?.data].map((app: Application) => {
        return { ...app };
      });
      dispatch(fetchApplicationDetailsSuccess(updatedApps[0]));
    } catch (error) {
      dispatch(
        setErrorNotification("Error while retrieving application details")
      );
      dispatch(fetchApplicationDetailsFailure(error));
    }
  };
};

export const userLogin = (
  fidp?: string,
  state?: string,
  correlationId?: string,
  orgName?: string,
  enterpriseEmail?: string,
) => {
  return (dispatch: Dispatch) => {
    dispatch(userLoginRequest());
    trackIdpAuthInitiation(correlationId || '');
    if (fidp === Settings.enterpriseFidp && enterpriseEmail) {
      authClient
        ?.signIn({
          fidp: fidp,
          username: enterpriseEmail,
          state: state || "",
      })
      .catch((error) => trackIdpAuthFailure(correlationId || '', error))
    } else if (
      fidp === Settings.enterpriseFidp && 
      orgName && isSelfSignupEnabled(orgName)
    ) {
      // Triggering enterprise login flow
      authClient
        ?.signIn({
          fidp: fidp,
          org: orgName,
          state: state || "",
        })
        .catch((error) => trackIdpAuthFailure(correlationId || '', error))
    } else if(fidp) {
      authClient
        ?.signIn({
          fidp: fidp,
          state: state || "",
        })
        .catch((error) => trackIdpAuthFailure(correlationId || '', error))
    } else {
      authClient
        ?.signIn()
        .catch((error) => trackIdpAuthFailure(correlationId || '', error));
    }

    authClient?.on(Hooks.SignIn, async (response: any) => {
      dispatch(userLoginSuccess(response));
      trackIdpAuthSuccess(correlationId || '');
      authClient
        ?.getDecodedIDToken()
        .then((idToken) => {
          dispatch(fetchUserInfoSuccess(idToken));
        })
        .catch((error) => {
          dispatch(
            setErrorNotification("Error while retrieving user information")
          );
          dispatch(fetchUserInfoFailure(error));
        });
    });

    // Sign out on auth failure
    authClient?.on(Hooks.HttpRequestError, (error) => {
      trackIdpAuthFailure(correlationId || '', error);
      const {
        response: { status, data: {code} },
      } = error;
      if (status === 401) {
        if (code && code === 1022) {
          dispatch(fetchUserOrgInfoFailure(error));
        } else {
          authClient.signOut();
        }
      }
    });
  };
};

export const fetchUserOrganizations = (correlationId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchUserOrgInfoRequest());
      const apiResponse = await getUserOrganizationList();
      if (apiResponse) {
        dispatch(fetchUserOrgInfoSuccess(apiResponse.data.organizations, apiResponse.status));
        trackFetchUserOrgSuccess(correlationId);
      }
    } catch (error) {
      // Avoid dispatching error message when the user register and signIn for the first time to an org which has enabled 
      // selfsignup as these errors are intended. This will apply for both Auto Approval enabled and disabled cases. 
      const code = error?.response?.data?.code;
      const type = error?.response?.data?.type;
      if (!((code === APPROVAL_STATUS_APPROVED_CODE && type === APPROVAL_STATUS_APPROVED) || 
            (code === USER_NOT_FOUND_CODE && type === USER_NOT_FOUND))) {
        dispatch(
          setErrorNotification("Error while retrieving organizations of user")
        );
      }
      dispatch(fetchUserOrgInfoFailure(error));
      trackFetchUserOrgFailure(correlationId, error);
    }
  };
};

export const obtainApimToken = (orgHandle: string, correlationId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(obtainApimTokenRequest());
    trackObtainApimTokenInitiation(correlationId);
    const config = {
      tokenEndpoint: Settings.tokenExchangeUrl,
      attachToken: false,
      data: {
        client_id: Settings.tokenExchangeConfig.clientId,
        grant_type: Settings.tokenExchangeConfig.grantType,
        subject_token_type: Settings.tokenExchangeConfig.subjectTokenType,
        requested_token_type: Settings.tokenExchangeConfig.requestedTokenType,
        scope: Settings.tokenExchangeConfig.scope,
        subject_token: "{{token}}",
        orgHandle: orgHandle,
      },
      id: "apim-token-exchange",
      returnResponse: true,
      returnsSession: true,
      signInRequired: true,
    };

    await authClient?.updateConfig({ validateIDToken: false });

    await authClient
      ?.requestCustomGrant(config)
      .then((response: any) => {
        dispatch(obtainApimTokenSuccess(response));
      })
      .catch((error) => () => {
        dispatch(setErrorNotification("Error while authenticating the user"));
        dispatch(obtainApimTokenFailure(error));
        trackObtainApimTokenFailure(correlationId, error);
      })
      .finally(async () => {
        await authClient.updateConfig({ validateIDToken: true });
      });

    authClient?.on(
      Hooks.CustomGrant,
      (response) => {
        dispatch(obtainApimTokenSuccess(response));
        trackObtainApimTokenSuccess(correlationId);
      },
      "apim-token-exchange"
    );
  };
};

export const obtainApimTokenAndChangeOrganization = (
  organization: Organization
) => {
  return async (dispatch: Dispatch) => {
    dispatch(obtainApimTokenRequest());
    const config = {
      tokenEndpoint: Settings.tokenExchangeUrl,
      attachToken: false,
      data: {
        client_id: Settings.tokenExchangeConfig.clientId,
        grant_type: Settings.tokenExchangeConfig.grantType,
        subject_token_type: Settings.tokenExchangeConfig.subjectTokenType,
        requested_token_type: Settings.tokenExchangeConfig.requestedTokenType,
        scope: Settings.tokenExchangeConfig.scope,
        subject_token: "{{token}}",
        orgHandle: organization.handle,
      },
      id: "apim-token-exchange",
      returnResponse: true,
      returnsSession: true,
      signInRequired: true,
    };

    await authClient?.updateConfig({ validateIDToken: false });

    await authClient
      ?.requestCustomGrant(config)
      .then((response: any) => {
        dispatch(obtainApimTokenSuccess(response));
        dispatch(changeUserOrganizationRequest());
        if (organization?.uuid) {
          dispatch(changeUserOrganizationSuccess(organization));
          Utils.localStoreOperation(
            StorageOperations.ADD,
            Constants.SELECTED_ORG_UUID,
            organization.uuid
          );
          Utils.localStoreOperation(
            StorageOperations.ADD,
            Constants.SELECTED_ORG_HANDLE_NAME,
            organization.handle
          );
        } else {
          const errorMessage = "Error while trying to change user organization";
          dispatch(changeUserOrganizationFailure(errorMessage));
          dispatch(setErrorNotification(errorMessage));
        }
      })
      .catch((error) => () => {
        dispatch(setErrorNotification("Error while authenticating the user"));
        dispatch(obtainApimTokenFailure(error));
      })
      .finally(async () => {
        await authClient.updateConfig({ validateIDToken: true });
      });

    authClient?.on(
      Hooks.CustomGrant,
      (response) => {
        dispatch(obtainApimTokenSuccess(response));
      },
      "apim-token-exchange"
    );
  };
};

export const fetchAPIVersionRangeSubscriptions =
(
  id: string,
  organizationId: string,
  reqHeaders = defaultHeaders,
) =>
async (dispatch: Dispatch) => {
  fetchSubscriptions(id, organizationId, "api", Constants.DEFAULT_APPLICATION_LIMIT, null, reqHeaders, true)(dispatch);
};

export const fetchSubscriptions =
  (
    id: string,
    organizationId: string,
    type = "api",
    limit?: number | null,
    offset?: number | null,
    reqHeaders = defaultHeaders,
    includeFromVersionRange = false,
  ) =>
  async (dispatch: Dispatch) => {
    const params: any = type === "api" ? { apiId: id } : { applicationId: id };
    if (limit !== null) {
      params['limit'] = limit;
    }
    if (offset !== null) {
      params['offset'] = offset;
    }
    dispatch(fetchSubscriptionRequest());
    try {
      const response = await authClient?.httpRequest({
        url: Utils.getSubscriptionResourceEndpoint(),
        method: "GET",
        params: {
          ...params,
          organizationId: organizationId,
          includeFromVersionRange: includeFromVersionRange || undefined,
        },
        headers: reqHeaders,
      });
      dispatch(fetchSubscriptionsSuccess(response?.data));
    } catch (error) {
      dispatch(
        setErrorNotification("Error while retrieving subscription details")
      );
      dispatch(fetchSubscriptionsFailure(error));
    }
  };

export const cleanupSubscriptions = () => async (dispatch: Dispatch) => {
    dispatch(fetchSubscriptionsSuccess({count: 0, list: [], pagination: {} as Pagination}));
}

export const fetchAppKeys =
  (appId: string, organizationId: string, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchKeysRequest());
    try {
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(appId, Constants.RESOURCE_APP_KEYS),
        method: "GET",
        headers: reqHeaders,
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(fetchKeysSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving application keys"));
      dispatch(fetchKeysFailure(error));
    }
  };

export const fetchApiCredentialKeys = (appId: string, organizationId: string, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchApiKeysCredentialsRequest());
    try {
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(appId, Constants.RESOURCE_APP_KEYS),
        method: "GET",
        headers: reqHeaders,
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(fetchApiKeysCredentialsSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving default application keys"));
      dispatch(fetchApiKeysCredentialsFailure(error));
    }
  };

export const generateApplicationToken = (
  applicationId: string,
  selectedScopes: string[],
  key: Key,
  validityPeriod = Settings.tokenValidityPeriod,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(createOauthTokenRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          applicationId,
          Constants.RESOURCE_APP_KEYS +
            Constants.RESOURCE_GENERATE_TOKEN.replace(
              "KEY_MAPPING_ID",
              key.keyMappingId
            )
        ),
        headers: reqHeaders,
        method: "POST",
        data: {
          consumerSecret: key.consumerSecret,
          validityPeriod,
          scopes: selectedScopes,
          revokeToken: "",
          additionalProperties: key.additionalProperties,
        },
        params: getOrgIdQueryParam(),
      });
      dispatch(createOauthTokenSuccess(response?.data));
      dispatch(setSucessNotification("Successfully created the access token"));
    } catch (error) {
      dispatch(setErrorNotification("Error while creating access token"));
      dispatch(createOauthTokenFailure(error));
    }
  };
};
export const exchangeToken = (
  applicationId: string,
  selectedScopes: string[],
  key: Key,
  additionalProperties: Record<string, unknown>,
  validityPeriod = Settings.tokenValidityPeriod,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(createOauthTokenRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          applicationId,
          Constants.RESOURCE_APP_KEYS +
            Constants.RESOURCE_GENERATE_TOKEN.replace(
              "KEY_MAPPING_ID",
              key.keyMappingId
            )
        ),
        headers: reqHeaders,
        method: "POST",
        data: {
          consumerSecret: key.consumerSecret,
          validityPeriod,
          scopes: selectedScopes,
          revokeToken: "",
          grantType: "TOKEN_EXCHANGE",
          additionalProperties: additionalProperties,
        },
        params: getOrgIdQueryParam(),
      });
      dispatch(createOauthTokenSuccess(response?.data));
      dispatch(setSucessNotification("Successfully created the access token"));
    } catch (error) {
      const status = error?.response?.status;
      if (status === Constants.BAD_REQUEST) {
        const errorDescription = error?.response?.data?.description;  
        dispatch(setErrorNotification(errorDescription));
      } else {
        dispatch(setErrorNotification("Error while creating access token"));
      }
      dispatch(createOauthTokenFailure(error));
    }
  };
};

export const generateApiKey = (
  applicationId: string,
  additionalProperties: ApiKeyAdditionalProperties,
  validityPeriod = Settings.tokenValidityPeriod,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(createApikeyRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApiKeyGenerationEndpoint(
          applicationId,
          Constants.ENVIRONMENTS.PRODUCTION
        ),
        headers: reqHeaders,
        method: "POST",
        data: { additionalProperties, validityPeriod },
        params: getOrgIdQueryParam(),
      });
      dispatch(createApikeySuccess(response?.data));
      dispatch(setSucessNotification("Successfully created the API key"));
    } catch (error) {
      dispatch(setErrorNotification("Error while creating API key"));
      dispatch(createApikeyFailure(error));
    }
  };
};

export const generateApiTestKey = (
  apiId: string,
  keyType: string,
  reqHeaders = defaultHeaders,
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(createApiTestKeyRequest())
      const response = await authClient?.httpRequest({
        url: Utils.getApiTestKeyGenerationEndpoint(
          apiId
        ),
        method: "POST",
        params:  {...getOrgIdQueryParam(), keyType: keyType},
        headers: reqHeaders,
      });
      dispatch(createApiTestKeySuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while generating test key"));
      dispatch(createApiTestKeyFailure(error));
    }
  };
}


export const generateToken = (
  selectedScopes: string[],
  defaultAppId: string,
  defaultAppKey: Key,
  validityPeriod = Settings.tokenValidityPeriod,
  reqHeaders = defaultHeaders
) => {
  const appId = defaultAppId;
  const appKeysInfo: Key = defaultAppKey;
  let organizationId = "";

  if (store.getState().userReducer.selectedOrg.uuid) {
    organizationId = store.getState().userReducer.selectedOrg.uuid;
  }
  return authClient?.httpRequest({
    url: Utils.getApplicationEndpoint(
      appId,
      Constants.RESOURCE_APP_KEYS +
        Constants.RESOURCE_GENERATE_TOKEN.replace(
          "KEY_MAPPING_ID",
          appKeysInfo.keyMappingId
        )
    ),
    headers: reqHeaders,
    method: "POST",
    data: {
      consumerSecret: appKeysInfo.consumerSecret,
      validityPeriod: validityPeriod,
      scopes: selectedScopes,
      revokeToken: "",
      additionalProperties: appKeysInfo.additionalProperties,
    },
    params: {
      organizationId: organizationId,
    },
  });
};

export const fetchSwagger = (
  apiId: string,
  organizationId: string,
  environmentName?: string,
  apiType?: string
) => {
  return async (dispatch: Dispatch) => {
    const definitionRequestId = startDefinitionRequest();
    try {
      dispatch(fetchSwaggerRequest());
      let queryParams = {};
      if (environmentName) {
        queryParams = {
          environmentName: environmentName,
        };
      }
      const response = await getApiResourceAuthenticated(
        apiId,
        getDefinitionResource(apiType),
        { ...queryParams, organizationId: organizationId },
        "json"
      );
      if (response && isLatestDefinitionRequest(definitionRequestId)) {
        dispatch(fetchSwaggerSuccess(response.data));
      }
    } catch (error) {
      if (!isLatestDefinitionRequest(definitionRequestId)) {
        return;
      }
      dispatch(
        setErrorNotification("Error while retrieving OpenAPI definition")
      );
      dispatch(fetchSwaggerFailure(error));
    }
  };
};

export const fetchSwaggerUnauthenticated =
  (apiId: string, organizationId: string, environmentName?: string, apiType?: string) =>
  async (dispatch: Dispatch) => {
    const definitionRequestId = startDefinitionRequest();
    try {
      dispatch(fetchSwaggerRequest());
      let queryParams = {};
      if (environmentName) {
        queryParams = {
          environmentName: environmentName,
        };
      }
      const response = await getApiResourceUnauthenticated(
        apiId,
        getDefinitionResource(apiType),
        { ...queryParams, organizationId: organizationId },
        "json"
      );
      if (response && isLatestDefinitionRequest(definitionRequestId)) {
        dispatch(fetchSwaggerSuccess(response.data));
      }
    } catch (error) {
      if (!isLatestDefinitionRequest(definitionRequestId)) {
        return;
      }
      dispatch(
        setErrorNotification("Error while retrieving OpenAPI definition")
      );
      dispatch(fetchSwaggerFailure(error));
    }
  };

export const postComment = (
  apiId: string,
  organizationId: string,
  comment: CreateComment
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(postApiCommentsRequest());
      const postResponse = await updateApiResourceAuthenticated(
        apiId,
        Constants.RESOURCE_API_COMMENTS,
        { organizationId: organizationId },
        comment,
        "POST"
      );
      if (postResponse) {
        dispatch(postApiCommentsSuccess(postResponse.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while adding the comment"));
      dispatch(postApiCommentsFailure(error));
    }
  };
};

export const editComment = (
    apiId: string,
    organizationId: string,
    commentId: string,
    comment: CreateComment
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(editApiCommentsRequest());
      const editCommentResource =
          Constants.RESOURCE_API_COMMENTS + "/" + commentId;
      const editResponse = await updateApiResourceAuthenticated(
          apiId,
          editCommentResource,
          {organizationId: organizationId},
          comment,
          "PATCH"
      );
      if (editResponse) {
        dispatch(editApiCommentsSuccess(editResponse.data));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while editing the comment"));
      dispatch(editApiCommentsFailure(error));
    }
  };
};

export const deleteComment = (
  apiId: string,
  organizationId: string,
  commentId: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(deleteApiCommentsRequest());
      const deleteCommentResource =
        Constants.RESOURCE_API_COMMENTS + "/" + commentId;
      const deleteResponse = await updateApiResourceAuthenticated(
        apiId,
        deleteCommentResource,
        { organizationId: organizationId },
        undefined,
        "DELETE"
      );
      if (deleteResponse) {
        dispatch(deleteApiCommentsSuccess(commentId));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while deleting the comment"));
      dispatch(deleteApiCommentsFailure(error));
    }
  };
};

export const postRating = (
  apiId: string,
  organizationId: string,
  rating: ApiRating
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(createRatingsRequest());
      const postResponse = await updateApiResourceAuthenticated(
        apiId,
        Constants.RESOURCE_USER_RATING,
        { organizationId: organizationId },
        rating,
        "PUT"
      );
      if (postResponse) {
        dispatch(createRatingsSuccess());
        // Get API rating
        try {
          dispatch(fetchRatingsRequest());
          const ratingsResponse = await getApiResourceAuthenticated(
            apiId,
            Constants.RESOURCE_API_RATINGS,
            { organizationId: organizationId }
          );
          if (ratingsResponse) {
            dispatch(fetchRatingsSuccess(ratingsResponse.data));
          }
        } catch (error) {
          dispatch(setErrorNotification("Error while retrieving API ratings"));
          dispatch(fetchRatingsFailure(error));
        }
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while updating API rating"));
      dispatch(createRatingsFailure(error));
    }
  };
};

export const fetchApplication =
  (appName: string, organizationId: string, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    const queryParams = {
      query: appName,
      organizationId: organizationId,
    };

    try {
      dispatch(fetchApplicationRequest());

      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(),
        method: "GET",
        headers: reqHeaders,
        params: queryParams,
      });
      dispatch(fetchApplicationSuccess(response?.data));
    } catch (error) {
      dispatch(
        setErrorNotification("Error while retrieving application details")
      );
      dispatch(fetchApplicationFailure(error));
    }
  };

export const createApplication = (
  application: Application,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch): Promise<string | null> => {
    try {
      dispatch(createApplicationRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(),
        method: "POST",
        headers: reqHeaders,
        data: {
          name: application.name,
          throttlingPolicy: application.throttlingPolicy,
          tokenType: application.tokenType,
          description: application?.description,
        },
        params: getOrgIdQueryParam(),
      });
      if (response) {
        dispatch(createApplicationSuccess(response.data));
        return response.data.applicationId;
      }
      return null;
    } catch (error) {
      dispatch(setErrorNotification("Error while creating application"));
      dispatch(createApplicationFailure(error));
      return null;
    }
  };
};

export const shareApplication = (
  application: Application, 
  applicationShareData: ApplicationShare,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch): Promise<any> => {
    try {
      dispatch(shareApplicationRequest(applicationShareData.applicationId, applicationShareData.userPermissions));
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationShareEndpoint(applicationShareData.applicationId, Constants.RESOURCE_SHARE_APPLICATION),
        method: "POST",
        headers: reqHeaders,
        data: {
          userPermissions: applicationShareData.userPermissions,
        },
        params: getOrgIdQueryParam()
      });
      if (response) {
        application.sharedWith = response.data.userPermissions;
        dispatch(shareApplicationSuccess(application));
        dispatch(setSucessNotification("Application shared successfully"));
        return response.data.applicationId;
      }
      return null;
    } catch (error) {
      dispatch(setErrorNotification("Error while sharing application"));
      dispatch(shareApplicationFailure(error));
      return null;
    }
  };
};

export const updateApplication =
  (application: Application, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(updateApplicationRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(application.applicationId),
        method: "PUT",
        headers: reqHeaders,
        data: application,
        params: getOrgIdQueryParam(),
      });
      const updatedApps = [response?.data].map((app: Application) => {
        return { ...app };
      });
      dispatch(updateApplicationSuccess(updatedApps[0]));
    } catch (error) {
      dispatch(setErrorNotification("Error while updating application"));
      dispatch(updateApplicationFailure(error));
    }
  };

export const deleteApplication = (
  applicationId: string,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(deleteApplicationRequest());
      await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(applicationId),
        method: "DELETE",
        headers: reqHeaders,
        params: getOrgIdQueryParam(),
      });
      dispatch(deleteApplicationSuccess(applicationId));
    } catch (error) {
      dispatch(setErrorNotification("Error while deleting application"));
      dispatch(deleteApplicationFailure(error));
    }
  };
};

export const createSubscription =
  (
    appId: string,
    organizationId: string,
    apiId: string,
    throttlingPolicy: string,
    versionRange?: string,
    message?: string,
    reqHeaders = defaultHeaders
  ) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(createSubscriptionsRequest());

      const response = await authClient?.httpRequest({
        url: Utils.getSubscriptionResourceEndpoint(),
        method: "POST",
        headers: reqHeaders,
        data: {
          applicationId: appId,
          apiId: apiId,
          throttlingPolicy: throttlingPolicy,
          versionRange: versionRange || undefined,
        },
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(createSubscriptionsSuccess(response?.data));
      if (typeof message === "string" && message.trim() !== "") {
        dispatch(setSucessNotification(message));
      } else {
        dispatch(setSucessNotification("Subscription created successfully"));
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while creating subscription"));
      dispatch(createSubscriptionsFailure(error));
    }
  };

export const createAppKeys =
  (
    appId: string,
    organizationId: string,
    grantTypes: string[],
    callBackUrl: string,
    additionalProperties: Record<string, unknown> = {},
    keyType = KeyType.PRODUCTION,
    keyManager = Settings.keyManager,
    reqHeaders = defaultHeaders
  ) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(createKeysRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          appId,
          Constants.RESOURCE_GENERATE_APP_KEYS
        ),
        method: "POST",
        headers: reqHeaders,
        data: {
          keyType: keyType,
          keyManager: keyManager,
          grantTypesToBeSupported: grantTypes,
          callbackUrl: callBackUrl,
          additionalProperties: additionalProperties,
        },
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(createKeysSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification(getCreateKeyErrorMessage(error)));
      dispatch(createKeysFailure(error));
    }
  };

export const createAppKeysAndToken =
  (
    appId: string,
    organizationId: string,
    grantTypes: string[],
    callBackUrl: string,
    scopes: string[],
    additionalProperties: Record<string, unknown> = {},
    keyType = KeyType.PRODUCTION,
    keyManager = Settings.keyManager,
    reqHeaders = defaultHeaders
  ) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(createKeysRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          appId,
          Constants.RESOURCE_GENERATE_APP_KEYS
        ),
        method: "POST",
        headers: reqHeaders,
        data: {
          keyType: keyType,
          keyManager: keyManager,
          grantTypesToBeSupported: grantTypes,
          callbackUrl: callBackUrl,
          additionalProperties: additionalProperties,
        },
        params: {
          organizationId: organizationId,
        },
      });
      if (response?.data?.keyMappingId) {
        const key = response.data;
        try {
          dispatch(createOauthTokenRequest());
          const response = await authClient?.httpRequest({
            url: Utils.getApplicationEndpoint(
              appId,
              Constants.RESOURCE_APP_KEYS +
                Constants.RESOURCE_GENERATE_TOKEN.replace(
                  "KEY_MAPPING_ID",
                  key.keyMappingId
                )
            ),
            headers: reqHeaders,
            method: "POST",
            data: {
              consumerSecret: key.consumerSecret,
              validityPeriod: Settings.tokenValidityPeriod,
              scopes: scopes,
              revokeToken: "",
              additionalProperties: key.additionalProperties,
            },
            params: getOrgIdQueryParam(),
          });
          dispatch(createOauthTokenSuccess(response?.data));
          dispatch(
            setSucessNotification("Successfully created the access token")
          );
        } catch (error) {
          dispatch(setErrorNotification("Error while creating access token"));
          dispatch(createOauthTokenFailure(error));
        }
      }
      dispatch(createKeysSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification(getCreateKeyErrorMessage(error)));
      dispatch(createKeysFailure(error));
    }
  };

export const updateApplicationKey =
  (applicationId: string, key: Key, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(updateKeysRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          applicationId,
          Constants.RESOURCE_APP_KEYS + "/" + key.keyMappingId
        ),
        headers: reqHeaders,
        method: "PUT",
        data: {
          keyManager: key.keyManager,
          keyMappingId: key.keyMappingId,
          consumerKey: key.consumerKey,
          consumerSecret: key.consumerSecret,
          supportedGrantTypes: key.supportedGrantTypes,
          callbackUrl: key.callbackUrl,
          keyType: key.keyType,
          additionalProperties: key.additionalProperties,
        },
        params: getOrgIdQueryParam(),
      });
      dispatch(updateKeysSuccess(response?.data));
      dispatch(setSucessNotification("Keys updated successfully"));
    } catch (error) {
      dispatch(setErrorNotification("Error while updating keys"));
      dispatch(updateKeysFailure(error));
    }
  };

export const updateAppKeys =
  (
    callBackUrl: string,
    grantTypes: string[],
    keyManager = Settings.keyManager,
    keyType = KeyType.PRODUCTION,
    reqHeaders = defaultHeaders
  ) =>
  async (dispatch: Dispatch) => {
    let appId = " ";
    let appKeysInfo: Key;
    let organizationId = "";
    try {
      dispatch(updateKeysRequest());

      if (store.getState().subscriptionsReducer.subscriptions.count > 0)
        appId =
          store.getState().subscriptionsReducer.subscriptions.list[0]
            .applicationId;
      else
        appId =
          store.getState().subscriptionsReducer.subscriptions.applicationId;

      if (store.getState().applicationKeysReducer.keys.count > 0)
        appKeysInfo = store.getState().applicationKeysReducer.keys.list[0];
      else appKeysInfo = store.getState().applicationKeysReducer.keys;

      if (store.getState().userReducer.selectedOrg.uuid) {
        organizationId = store.getState().userReducer.selectedOrg.uuid;
      }
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(
          appId,
          Constants.RESOURCE_APP_KEYS + "/" + appKeysInfo.keyMappingId
        ),
        headers: reqHeaders,
        method: "PUT",
        data: {
          keyManager: keyManager,
          keyMappingId: appKeysInfo.keyMappingId,
          consumerKey: appKeysInfo.consumerKey,
          consumerSecret: appKeysInfo.consumerSecret,
          supportedGrantTypes: grantTypes,
          callbackUrl: callBackUrl,
          keyType: keyType,
        },
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(updateKeysSuccess(response?.data));
      dispatch(setSucessNotification("Keys updated successfully"));
    } catch (error) {
      dispatch(setErrorNotification("Error while updating keys"));
      dispatch(updateKeysFailure(error));
    }
  };

export const generateCredentials =
  (
    apiId: string,
    appName: string,
    organizationId: string,
    subThrottlingPolicy: string,
    grantTypes: string[],
    callBackUrl: string,
    appThrottlingPolicy = Settings.defaultAppThrottlingPolicy,
    keyType = KeyType.PRODUCTION,
    keyManager = Settings.keyManager,
    tokenType = Settings.defaultAppTokenType,
    reqHeaders = defaultHeaders
  ) =>
  async (dispatch: Dispatch) => {
    let applicationId = "";
    let subscriptionId = "";

    dispatch(createApiKeysCredentialsRequest());

    const queryParams = {
      query: appName,
      organizationId: organizationId,
    };

    // Fetch or create Application for given app name
    try {
      dispatch(fetchApplicationRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(),
        method: "GET",
        headers: reqHeaders,
        params: queryParams,
      });
      dispatch(fetchApplicationSuccess(response?.data));

      if (response?.data.count > 0) {
        applicationId = response?.data.list[0].applicationId;
      } else {
        try {
          dispatch(createApplicationRequest());
          const response = await authClient?.httpRequest({
            url: Utils.getApplicationEndpoint(),
            method: "POST",
            headers: reqHeaders,
            data: {
              name: appName,
              throttlingPolicy: appThrottlingPolicy,
              tokenType: tokenType,
            },
            params: {
              organizationId: organizationId,
            },
          });
          dispatch(createApplicationSuccess(response?.data));
          applicationId = response?.data.applicationId;
        } catch (error) {
          dispatch(createApplicationFailure(error));
          dispatch(setErrorNotification("Credentials generation failed"));
          dispatch(
            createApiKeysCredentialsFailure(
              "Credentials generation failed while creating application"
            )
          );
        }
      }

      //Fetch or create Subscription
      if (applicationId) {
        try {
          dispatch(fetchSubscriptionRequest());
          const response = await authClient?.httpRequest({
            url: Utils.getSubscriptionResourceEndpoint(),
            method: "GET",
            params: {
              apiId: apiId,
              organizationId: organizationId,
            },
            headers: reqHeaders,
          });
          dispatch(fetchSubscriptionsSuccess(response?.data));

          const defaultSubscription = response?.data.list.find(
            (subscription: Subscription) =>
              subscription.applicationId === applicationId
          );
          if (defaultSubscription) {
            subscriptionId = defaultSubscription.subscriptionId;
          } else {
            try {
              dispatch(createSubscriptionsRequest());
              const response = await authClient?.httpRequest({
                url: Utils.getSubscriptionResourceEndpoint(),
                method: "POST",
                headers: reqHeaders,
                data: {
                  applicationId: applicationId,
                  apiId: apiId,
                  throttlingPolicy: subThrottlingPolicy,
                },
                params: {
                  organizationId: organizationId,
                },
              });
              dispatch(createSubscriptionsSuccess(response?.data));
              subscriptionId = response?.data.subscriptionId;
            } catch (error) {
              dispatch(createSubscriptionsFailure(error));
              dispatch(setErrorNotification("Credentials generation failed"));
              dispatch(
                createApiKeysCredentialsFailure(
                  "Credentials generation failed while creating subscription"
                )
              );
            }
          }

          //Fetch or create Application keys
          if (subscriptionId) {
            try {
              dispatch(fetchApiKeysCredentialsRequest());
              const response = await authClient?.httpRequest({
                url: Utils.getApplicationEndpoint(
                  applicationId,
                  Constants.RESOURCE_APP_KEYS
                ),
                method: "GET",
                headers: reqHeaders,
                params: {
                  organizationId: organizationId,
                },
              });
              dispatch(fetchApiKeysCredentialsSuccess(response?.data));

              let envKey;
              if(response?.data.count > 0) {
                envKey = response?.data.list.find((key: Key) => key.keyType === keyType);
              }

              if (envKey) {
                dispatch(createApiKeysCredentialsSuccess(response?.data));
              } else {
                try {
                  const response = await authClient?.httpRequest({
                    url: Utils.getApplicationEndpoint(
                      applicationId,
                      Constants.RESOURCE_GENERATE_APP_KEYS
                    ),
                    method: "POST",
                    headers: reqHeaders,
                    data: {
                      keyType: keyType,
                      keyManager: keyManager,
                      grantTypesToBeSupported: grantTypes,
                      callbackUrl: callBackUrl,
                    },
                    params: {
                      organizationId: organizationId,
                    },
                  });
                  dispatch(createApiKeysCredentialsSuccess(response?.data));
                } catch (error) {
                  dispatch(setErrorNotification(getCreateKeyErrorMessage(error, "Credentials generation failed")));
                  dispatch(
                    createApiKeysCredentialsFailure(
                      "Credentials generation failed while creating application keys"
                    )
                  );
                }
              }
            } catch (error) {
              dispatch(fetchApiKeysCredentialsFailure(error));
              dispatch(setErrorNotification("Credentials generation failed"));
              dispatch(
                createApiKeysCredentialsFailure(
                  "Credentials generation failed while retrieving application keys"
                )
              );
            }
          }
        } catch (error) {
          dispatch(fetchSubscriptionsFailure(error));
          dispatch(setErrorNotification("Credentials generation failed"));
          dispatch(
            createApiKeysCredentialsFailure(
              "Credentials generation failed while retrieving subscription details"
            )
          );
        }
      }
    } catch (error) {
      dispatch(fetchApplicationFailure(error));
      dispatch(setErrorNotification("Credentials generation failed"));
      dispatch(
        createApiKeysCredentialsFailure(
          "Credentials generation failed while retrieving application details"
        )
      );
    }
  };

// @TODO - Dispatch type doesn't suit parameter dispatch and need to replace with a specific type
export const checkCredentials = () => async (dispatch: any) => {
  try {
    const apiId = store.getState().apiReducer.api.id;
    const apiName = store.getState().apiReducer.api.name;
    const organizationId = store.getState().userReducer.selectedOrg.uuid;
    dispatch(fetchApiKeysCredentialsRequest());
    await dispatch(fetchSubscriptions(apiId, organizationId));

    const defaultAppName =
      Constants.DEFAULT_APP_INTERNAL_PREFIX +
      store.getState().userReducer.userInfo.sub +
      "_" +
      apiName;
    const defaultSubscription = store
      .getState()
      .subscriptionsReducer.subscriptions.list.find(
        (subscription: Subscription) =>
          subscription.applicationInfo?.name === defaultAppName
      );
    if (defaultSubscription) {
      const appId = defaultSubscription.applicationInfo?.applicationId;
      await dispatch(fetchApiCredentialKeys(appId, organizationId));
    } else {
      dispatch(fetchApiKeysCredentialsFailure("Default Credentials Unavailable"));
    }
  } catch (error) {
    dispatch(fetchApiKeysCredentialsFailure("Credentials Check Failed " + error));
  }
};

export const generateSdk = (
  apiId: string,
  organizationId: string,
  language: string
) => {
  const headers = {
    Accept: "*/*",
  };
  return getApiResourceAuthenticated(
    apiId,
    Constants.RESOURCE_GENERATE_SDK.replace("LANGUAGE", language),
    { organizationId: organizationId },
    "blob",
    headers
  );
};

export const getSdkLanguagesResource = async (organizationId: string) => {
  return authClient?.httpRequest({
    headers: {
      Accept: "application/json",
    },
    method: "GET",
    url: Utils.getSdkResourceEndpoint(),
    params: {
      organizationId: organizationId,
    },
  });
};

export const getApiDocumentContent = (documentId: string, isFileType: boolean, isLoggedIn: boolean) => {
  const apiId = store.getState().apiReducer.api.id;
  const organizationId = store.getState().userReducer.selectedOrg.uuid;
  if (isLoggedIn) {
    return getApiResourceAuthenticated(
        apiId,
        Constants.RESOURCE_API_DOCUMENTS +
        Constants.RESOURCE_API_DOCUMENT_CONTENT.replace(
            "DOCUMENT_ID",
            documentId
        ),
        { organizationId: organizationId },
        isFileType ? "blob" : "text"
    );
  } else {
    const orgId = store.getState().organizationMetadataReducer.organizationMetadata.uuid;
    return getApiResourceUnauthenticated(
        apiId,
        Constants.RESOURCE_API_DOCUMENTS +
        Constants.RESOURCE_API_DOCUMENT_CONTENT.replace(
            "DOCUMENT_ID",
            documentId
        ),
        { organizationId: orgId },
        isFileType ? "blob" : "text"
    );
  }
};

export const updateSubscription = (
  subscriptionId: string,
  subscription: Subscription,
  organizationId: string,
  message?: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(updateSubscriptionRequest());
      const subscriptionResponse = await updateSubscriptionAuthenticated(
        subscriptionId,
        subscription,
        { organizationId: organizationId }
      );
      if (subscriptionResponse) {
        dispatch(updateSubscriptionSuccess(subscriptionResponse.data));
        if (typeof message === "string" && message.trim() !== "") {
          dispatch(setSucessNotification(message));
        } else {
          dispatch(setSucessNotification("Subscription updated successfully"));
        }
      }
    } catch (error) {
      dispatch(setErrorNotification("Error while updating subscription"));
      dispatch(updateSubscriptionFailure(error));
    }
  };
};

export const deleteSubscription = (
  subscriptionId: string,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(deleteSubscriptionsRequest());
      await authClient?.httpRequest({
        url: Utils.getSubscriptionResourceEndpoint(subscriptionId),
        method: "DELETE",
        headers: reqHeaders,
        params: getOrgIdQueryParam(),
      });
      dispatch(deleteSubscriptionsSuccess(subscriptionId));
      dispatch(setSucessNotification("Subscription deleted successfully"));
    } catch (error) {
      dispatch(setErrorNotification("Error while deleting subscription"));
      dispatch(deleteSubscriptionsFailure(error));
    }
  };
};

export const removeDefaultApplicationKeys = (
  keyType: KeyType,
  appName: string,
  reqHeaders = defaultHeaders
) => async (dispatch: Dispatch) => {
  try {
    dispatch(deleteApiKeysCredentialsRequest());
    let organizationId;
    if (store.getState().userReducer.selectedOrg.uuid) {
      organizationId = store.getState().userReducer.selectedOrg.uuid;
    }
    await store.dispatch(fetchApplication(appName, organizationId));
    const defaultApp = store.getState().applicationReducer.applications.list.find(
      (app: Application) => app.name.includes(Constants.DEFAULT_APP_INTERNAL_PREFIX, 0)
    );
    const deletingKey = store.getState().credentialsReducer.keys.list.find((key: Key) => key.keyType === keyType);
    

    if (!defaultApp || !deletingKey || !organizationId) {
      dispatch(setErrorNotification("Error while removing credentials"));
      dispatch(deleteApiKeysCredentialsFailure("Credentials deletion failed"));
      return;
    }

    await authClient?.httpRequest({
      url: Utils.getApplicationEndpoint(
        defaultApp.applicationId, Constants.RESOURCE_APP_KEYS
      + Constants.RESOURCE_CLEAN_UP_APP_KEYS.replace("KEY_MAPPING_ID", deletingKey.keyMappingId)
      ),
      method: "POST",
      headers: reqHeaders,
      params: {
        organizationId: organizationId,
      },
    });
    const remainingKeys = store.getState().credentialsReducer.keys.list.filter((key: Key) => key.keyType !== keyType);
    dispatch(deleteApiKeysCredentialsSuccess({ count: remainingKeys.length, list: remainingKeys }));
  } catch (error) {
    dispatch(setErrorNotification("Error while removing credentials"));
    dispatch(deleteApiKeysCredentialsFailure("Credentials deletion failed"));
  }
}

export const removeCredentials =
  (appName: string, reqHeaders = defaultHeaders) =>
  async (dispatch: Dispatch) => {
    let appId = "";
    let organizationId = "";
    if (store.getState().userReducer.selectedOrg.uuid) {
      organizationId = store.getState().userReducer.selectedOrg.uuid;
    }
    try {
      dispatch(deleteApplicationRequest());
      dispatch(deleteApiKeysCredentialsRequest());
      await store.dispatch(fetchApplication(appName, organizationId));

      if (store.getState().applicationReducer.applications.count > 0) {
        appId =
          store.getState().applicationReducer.applications.list[0]
            .applicationId;
      } else {
        appId = store.getState().applicationReducer.applications.applicationId;
      }

      await authClient?.httpRequest({
        url: Utils.getApplicationEndpoint(appId),
        method: "DELETE",
        headers: reqHeaders,
        params: {
          organizationId: organizationId,
        },
      });
      dispatch(deleteApplicationSuccess(appId));
      dispatch(deleteApiKeysCredentialsSuccess(null));
    } catch (error) {
      dispatch(setErrorNotification("Error while removing credentials"));
      dispatch(deleteApplicationFailure(error));
      dispatch(
        deleteApiKeysCredentialsFailure(
          "Credentials deletion failed while deleting Application"
        )
      );
    }
  };

export const resetNotificationMsg = () => {
  return (dispatch: Dispatch) => {
    dispatch(resetNotification());
  };
};

export const changeUserOrganization = (selectedOrganization: Organization) => {
  return (dispatch: Dispatch) => {
    dispatch(changeUserOrganizationRequest());
    if (selectedOrganization?.uuid) {
      dispatch(changeUserOrganizationSuccess(selectedOrganization));
      Utils.localStoreOperation(
        StorageOperations.ADD,
        Constants.SELECTED_ORG_UUID,
        selectedOrganization.uuid
      );
      Utils.localStoreOperation(
        StorageOperations.ADD,
        Constants.SELECTED_ORG_HANDLE_NAME,
        selectedOrganization.handle
      );
    } else {
      const errorMessage = "Error while trying to change user organization";
      dispatch(changeUserOrganizationFailure(errorMessage));
      dispatch(setErrorNotification(errorMessage));
    }
  };
};

/* Fetch Identity provider list */
export const fetchIDPs = (
  organizationId: string,
  devPortalAppEnv?: DevPortalAppEnv,
  reqHeaders = defaultHeaders
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchIDPRequest());

      const response = await authClient?.httpRequest({
        url: Utils.getIDPsEndpoint(),
        method: "GET",
        headers: reqHeaders,
        params: {
          organizationId: organizationId,
          devPortalAppEnv: devPortalAppEnv?.toString(),
        },
      });
      dispatch(fetchIDPSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while fetching IDPs"));
      dispatch(fetchIDPFailure(error));
    }
  };
};

export const addExternalUserToOrg = (organizationHandle: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(addExternalUserToOrgRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getAddExternalUserToOrgEndpoint(organizationHandle),
        headers: defaultHeaders,
        method: "POST",
      });
      if (response) {
        dispatch(addExternalUserToOrgSuccess());
        return response.status;
      }
    } catch (error) {
      dispatch(
        setErrorNotification(
          "Error while adding user to the requested organization"
        )
      );
      dispatch(addExternalUserToOrgFailure(error));
    }
  };
};

export const verifyInvitation = (confirmationOrg: string, confirmationKey: string) => {
  return axios.get(
      Utils.getGetInvitationEndpoint(confirmationOrg, confirmationKey),
      {
        headers: {'Content-Type': 'application/json'},
      }
  );
};

export const acceptInvitation = (confirmationOrg: string, confirmationKey: string) => {
  return authClient?.httpRequest({
    url: Utils.getAcceptInvitationEndpoint(confirmationOrg, confirmationKey),
    headers: defaultHeaders,
    method: "POST"
  });
};

export const addEnterpriseUserToOrg = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(addEnterpriseUserRequest());
      const apiResponse = await addEnterpriseUser();
      if (apiResponse) {
        dispatch(addEnterpriseUserSuccess(apiResponse.data.organizations));
      }
    } catch (error) {
      dispatch(
        setErrorNotification("Error while registering user")
      );
      dispatch(addEnterpriseUserFailure(error));
    }
  };
};

/* Maping application keys with the external IDP */
export const mapApplicationKeys = (
  applicationId: string,
  payload: ApplicationKeyMapping,
  reqHeaders = defaultHeaders,
  queryParams = getOrgIdQueryParam(),
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(mapApplicationKeyRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApplicationKeysMapEndpoint(applicationId),
        method: "POST",
        headers: reqHeaders,
        params: queryParams,
        data: payload
      });
      dispatch(mapApplicationKeySucess(response?.data));
      dispatch(setSucessNotification("Client ID added successfully"));
    } catch (error) {
      if (error.response?.data?.code == 901409) {
        dispatch(setErrorNotification("Client already registered with the organization"));
      } else {
        dispatch(setErrorNotification("Error while setting client ID"));
      }
      dispatch(mapApplicationKeyFailure(error));
    }
  };
};

/* List api keys */
export const fetchApiKeysList = (
  apiId: string,
  applicationId: string,
  organizationId: string,
  keyType: string,
  reqHeaders = defaultHeaders) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchApiKeysRequest());
      const response = await authClient?.httpRequest({
        url: Utils.getApiKeysEndpoint(apiId, applicationId, organizationId, keyType),
        method: "GET",
        headers: reqHeaders,
        params:  {},
      });
      dispatch(fetchApiKeysSuccess(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while retrieving api keys"));
      dispatch(fetchApiKeysFailure(error));
    }
  };
}

/* Generate API Keys */
export const generateApiKeyV2 = (
  payLoad: ApiKeyGenerationRequest,
  reqHeaders = defaultHeaders) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(generateApiKeyV2Request());
      const response = await authClient?.httpRequest({
        url: Utils.getApiKeyGenerationV2Endpoint(),
        method: "POST",
        headers: reqHeaders,
        params: getOrgIdQueryParam(),
        data: payLoad
      });
      dispatch(generateApiKeyV2Success(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while generating api keys"));
      dispatch(generateApiKeyV2Failure(error));
    }
  };
}

export const regenerateApiKeyV2 = (
  apiKeyId: string,
  reqHeaders = defaultHeaders) => {
    return async (dispatch: Dispatch) => {
    try {
      dispatch(regenerateApiKeyV2Request());
      const response = await authClient?.httpRequest({
        url: Utils.getApiKeyRegenerationV2Endpoint(apiKeyId),
        method: "POST",
        headers: reqHeaders,
        params: getOrgIdQueryParam(),
        data: {}
      });
      dispatch(regenerateApiKeyV2Success(response?.data));
    } catch (error) {
      dispatch(setErrorNotification("Error while regenerating api keys"));
      dispatch(regenerateApiKeyV2Failure(error));
    }
  };
}

export const revokeApiKeyV2 = (
  apiKeyId: string,
  reqHeaders = defaultHeaders) => {
    return async (dispatch: Dispatch) => {
    try {
      dispatch(revokeApiKeyV2Request());
      const response = await authClient?.httpRequest({
        url: Utils.getApiKeyRovokeV2Endpoint(apiKeyId),
        method: "POST",
        headers: reqHeaders,
        params: getOrgIdQueryParam(),
        data: {}
      });
      dispatch(revokeApiKeyV2Success());
    } catch (error) {
      dispatch(setErrorNotification("Error while deleting the api key"));
      dispatch(revokeApiKeyV2Failure(error));
    }
  };
}

const getCreateKeyErrorMessage = (error: AxiosError, customError?: string) => {
  if (error.response && error.response.data) {
    if (error.response.data.code === 130001) {
      return error.response.data.description;
    }
  }
  return customError || "Error while creating application keys";
}
